import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { sanitizeSchema, urlTransform } from '../comments/MarkdownBody';
import { githubSlug, resolveDocLink } from './docLinks';

/**
 * MKS-508 — sanitized markdown renderer for the company docs.
 *
 * Same sanitization schema and URL transform as `MarkdownBody` (exported from
 * there so the two cannot drift); no raw HTML, images render as nothing. The
 * typography is document-sized rather than comment-sized.
 *
 * Links (D-06): a link to another public doc navigates in-app, `#anchor` links
 * scroll to GitHub-style heading ids, http(s) links open in a new tab, and any
 * other relative link (a private doc, a repo file) is non-clickable text.
 */
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

function textOf(node: HastNode): string {
  if (node.type === 'text') return node.value ?? '';
  return (node.children ?? []).map(textOf).join('');
}

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

/**
 * Sets GitHub-style ids on headings. Listed AFTER rehype-sanitize so the
 * sanitizer's `user-content-` clobber prefix never touches them; the ids are
 * slugs we generate, never author-supplied values.
 */
function rehypeHeadingIds() {
  return (tree: HastNode) => {
    const seen = new Map<string, number>();
    const walk = (node: HastNode) => {
      if (node.type === 'element' && node.tagName && HEADING_TAGS.has(node.tagName)) {
        node.properties = { ...node.properties, id: githubSlug(textOf(node), seen) };
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

const linkClass = 'text-primary-600 underline break-words';

const baseComponents: Components = {
  img: () => null,
  h1: ({ children, id }) => (
    <h1 id={id} className="text-3xl font-bold text-gray-900 mt-2 mb-4">
      {children}
    </h1>
  ),
  h2: ({ children, id }) => (
    <h2 id={id} className="text-2xl font-semibold text-gray-900 mt-8 mb-3 pb-1 border-b border-gray-200">
      {children}
    </h2>
  ),
  h3: ({ children, id }) => (
    <h3 id={id} className="text-xl font-semibold text-gray-900 mt-6 mb-2">
      {children}
    </h3>
  ),
  h4: ({ children, id }) => (
    <h4 id={id} className="text-base font-semibold text-gray-900 mt-4 mb-2">
      {children}
    </h4>
  ),
  h5: ({ children, id }) => (
    <h5 id={id} className="text-base font-medium text-gray-900 mt-4 mb-1">
      {children}
    </h5>
  ),
  h6: ({ children, id }) => (
    <h6 id={id} className="text-sm font-medium text-gray-600 mt-4 mb-1">
      {children}
    </h6>
  ),
  p: ({ children }) => <p className="my-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="my-3 ml-6 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 ml-6 list-decimal space-y-1">{children}</ol>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-4 border-gray-300 pl-4 text-gray-600">{children}</blockquote>
  ),
  hr: () => <hr className="my-6 border-gray-200" />,
  code: ({ className, children }) =>
    className ? (
      <code className={className}>{children}</code>
    ) : (
      <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em] font-mono text-gray-800">
        {children}
      </code>
    ),
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-md bg-gray-900 p-4 text-sm text-gray-100">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-300 bg-gray-50 px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-gray-300 px-3 py-2 align-top">{children}</td>,
};

export interface DocMarkdownProps {
  markdown: string;
  /** Slugs of the listed public docs; a relative link to one navigates in-app. */
  slugs: ReadonlySet<string>;
}

export function DocMarkdown({ markdown, slugs }: DocMarkdownProps) {
  const components = useMemo<Components>(
    () => ({
      ...baseComponents,
      a: ({ href, children }) => {
        const link = resolveDocLink(href, slugs);
        switch (link.kind) {
          case 'doc':
            return (
              <Link to={link.to} className={linkClass}>
                {children}
              </Link>
            );
          case 'anchor':
            return (
              <a href={link.href} className={linkClass}>
                {children}
              </a>
            );
          case 'external':
            return (
              <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {children}
              </a>
            );
          default:
            return (
              <span title="Repository only" className="text-gray-500 cursor-help">
                {children}
              </span>
            );
        }
      },
    }),
    [slugs],
  );

  return (
    <div className="text-gray-800">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema], rehypeHeadingIds]}
        urlTransform={urlTransform}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
